/**
 * LE DUE SCELTE DELLA SFIDA A BACKGAMMON (I-459): «Lunghezza» e «Orologio».
 *
 * Luca, 05/10 ~22:24Z: «quando invio sfida ad amico e seleziono backgammon, non ho selezione sfida be1 be3 be5 senza
 * clock». Il provider serve una voce per ogni coppia lunghezza x orologio (9 oggi, 12 col «senza»): in fila sono troppe,
 * e fra loro nessuna e' «senza orologio». Qui le voci servite si riducono a DUE assi, e la coppia scelta torna a
 * essere UNA voce servita: se ne manda la `key`, che resta il contratto dell'invio (`POST /v1/challenges`).
 *
 * ═══ 🔑 COME SI LEGGE UNA VOCE ═══
 * 1. Se la facciata manda i campi (kb#369), la lunghezza viene da `matchLength` e il «senza» da
 *    `timeControl.unlimited === true`. Il NOME del controllo con orologio resta il suffisso della `key`.
 * 2. Oggi i campi NON arrivano (`sanitizeGameMatchModes`, `qlash-limulo@staging` 3dc0d80f,
 *    `app-qlash-api/backend/game-match-modes.js:9-13`: lascia passare solo key, label, status, teamSize,
 *    teamsPerMatch, platformIds). Si legge la `key` con la forma `backgammon-<n>pt-<controllo>`, che e' la
 *    STESSA della create del provider (`app-qgammon/backend/provider.js:1224`, `^backgammon-(\d+)pt-(blitz|rapid|classic)$`,
 *    dove `matchLength` esplicito vince, :1223). ⚠️ Il provider scrive a :302-303 che «una chiave e' un'etichetta,
 *    non un formato»: il ripiego sulla chiave e' una scelta nostra, non un contratto, e per questo e' STRETTO.
 *    Non e' il valore a essere scritto qui (nessun `3`, nessun `rapid` nella regola): e' la FORMA della chiave.
 * 3. Cio' che non si capisce NON si indovina: se anche UNA sola voce servita ha una forma diversa, un controllo
 *    fuori da quelli noti, un campo che contraddice la chiave, o una coppia ripetuta, `assiDelBackgammon` torna
 *    `null` e il foglio resta alla fila di oggi. Niente letture a meta'.
 *
 * Il valore noto di un controllo e' quello che ha un testo nei cataloghi (`social.sfida_bg_orologio_*`) e il suo
 * posto a schermo: Senza · Blitz · Rapid · Classic. Un valore che il BFF non serve non compare mai.
 *
 * Il modulo e' PURO (nessun React, nessun `import.meta.env`): un banco lo esegue sotto Node (type-stripping).
 */
import type { LimuloGameMatchMode } from '../../types/limulo.ts';

/** I controlli noti, nell'ORDINE in cui stanno a schermo. Ognuno ha il suo testo nei 4 cataloghi. */
export const CONTROLLI_BACKGAMMON = ['unlimited', 'blitz', 'rapid', 'classic'] as const;
export type ControlloBackgammon = (typeof CONTROLLI_BACKGAMMON)[number];

/** La forma della `key` (provider.js:1224), allargata a ogni suffisso: se il suffisso e' noto lo decide `leggiVoce`. */
const FORMA_CHIAVE = /^backgammon-(\d+)pt-([a-z]+)$/;

const controlloNoto = (s: string): s is ControlloBackgammon => (CONTROLLI_BACKGAMMON as readonly string[]).includes(s);
const interoDa = (v: unknown, minimo: number): v is number => typeof v === 'number' && Number.isInteger(v) && v >= minimo;

/** Una modalita' servita, letta in coppia (lunghezza, controllo). */
export interface VoceBackgammon {
  key: string;
  lunghezza: number;
  controllo: ControlloBackgammon;
}

/** Le voci servite e i due assi che ne escono, gia' nell'ordine di schermo. */
export interface AssiBackgammon {
  voci: VoceBackgammon[];
  /** Le lunghezze servite, in ordine crescente. */
  lunghezze: number[];
  /** I controlli serviti, nell'ordine di `CONTROLLI_BACKGAMMON`. */
  controlli: ControlloBackgammon[];
}

/** Quello che serve a leggere una voce: la `key` e, se la facciata li manda, i due campi. */
export type ModalitaLetta = Pick<LimuloGameMatchMode, 'key'> & Partial<Pick<LimuloGameMatchMode, 'matchLength' | 'timeControl'>>;

/**
 * Una voce servita come coppia, oppure `null` se non si capisce o i campi contraddicono la chiave.
 * `matchLength` e `timeControl` si leggono come `unknown`: il parser di Games non valida a runtime (`unwrapItems`).
 */
function leggiVoce(m: ModalitaLetta): VoceBackgammon | null {
  const dallaChiave = FORMA_CHIAVE.exec(m.key);
  if (dallaChiave === null) return null;
  const lunghezza = Number(dallaChiave[1]);
  const controllo = dallaChiave[2];
  if (!interoDa(lunghezza, 1) || !controlloNoto(controllo)) return null;

  const lunghezzaDichiarata: unknown = m.matchLength;
  // Basta il confronto: la chiave e' gia' un intero >= 1, quindi zero, 3.5, '3' o 5 su `3pt` sono tutti «diverso».
  if (lunghezzaDichiarata !== undefined && lunghezzaDichiarata !== null && lunghezzaDichiarata !== lunghezza) return null;

  const orologioDichiarato: unknown = m.timeControl;
  if (orologioDichiarato !== undefined && orologioDichiarato !== null) {
    // Un valore che non e' un oggetto (una stringa, un array) non ha i campi: cade nel ramo dei secondi e li' esce `null`.
    const campi = orologioDichiarato as Record<string, unknown>;
    if (campi.unlimited === true) {
      if (controllo !== 'unlimited') return null;
    } else {
      if (!interoDa(campi.bankSeconds, 0) || !interoDa(campi.incrementSeconds, 0)) return null;
      if (controllo === 'unlimited') return null;
    }
  }
  return { key: m.key, lunghezza, controllo };
}

/**
 * I due assi delle modalita' SERVITE (e inviabili: il chiamante ha gia' filtrato per `teamSize`), oppure `null` se la
 * regola non le sa leggere TUTTE (v. il cappello): allora il foglio resta alla fila. Una `key` vuota o ripetuta si scarta
 * come in `modalitaDellaSfida`, cosi' fila e assi vedono le stesse voci. Un elenco vuoto non ha assi.
 */
export function assiDelBackgammon(modalita: readonly ModalitaLetta[]): AssiBackgammon | null {
  const viste = new Set<string>();
  const coppie = new Set<string>();
  const voci: VoceBackgammon[] = [];
  for (const m of modalita) {
    if (m.key === '' || viste.has(m.key)) continue;
    viste.add(m.key);
    const voce = leggiVoce(m);
    if (voce === null) return null;
    const coppia = `${voce.lunghezza}|${voce.controllo}`;
    if (coppie.has(coppia)) return null;
    coppie.add(coppia);
    voci.push(voce);
  }
  if (voci.length === 0) return null;
  return {
    voci,
    lunghezze: [...new Set(voci.map((v) => v.lunghezza))].sort((a, b) => a - b),
    controlli: CONTROLLI_BACKGAMMON.filter((c) => voci.some((v) => v.controllo === c)),
  };
}

/** Cio' che l'utente ha scelto sui due assi: `null` = non ancora scelto. */
export interface SceltaAssi {
  lunghezza: number | null;
  controllo: ControlloBackgammon | null;
}

export const NESSUNA_SCELTA: SceltaAssi = { lunghezza: null, controllo: null };

/** La `key` della coppia scelta: quella della voce servita, oppure `null` (scelta incompleta o coppia non servita). */
export function chiaveDellaScelta(assi: AssiBackgammon, scelta: SceltaAssi): string | null {
  return assi.voci.find((v) => v.lunghezza === scelta.lunghezza && v.controllo === scelta.controllo)?.key ?? null;
}

/**
 * Una scelta a mano vale finche' il server serve ancora cio' che ha scelto: ogni asse scelto e' fra i serviti
 * e, a scelta completa, la coppia e' una voce servita. Altrimenti `null` e torna la preselezione, come la fila
 * (`valida` in `InvioModalita`): una rilettura che toglie una voce non lascia in piedi una scelta che il server non ha.
 */
export function sceltaAncoraServita(assi: AssiBackgammon, scelta: SceltaAssi | null): SceltaAssi | null {
  if (scelta === null) return null;
  if (scelta.lunghezza !== null && !assi.lunghezze.includes(scelta.lunghezza)) return null;
  if (scelta.controllo !== null && !assi.controlli.includes(scelta.controllo)) return null;
  if (scelta.lunghezza !== null && scelta.controllo !== null && chiaveDellaScelta(assi, scelta) === null) return null;
  return scelta;
}

/** La scelta che corrisponde a una `key` servita (la preselezione); `NESSUNA_SCELTA` se la `key` non e' servita. */
export function sceltaDellaChiave(assi: AssiBackgammon, key: string | null): SceltaAssi {
  const voce = key === null ? undefined : assi.voci.find((v) => v.key === key);
  return voce === undefined ? NESSUNA_SCELTA : { lunghezza: voce.lunghezza, controllo: voce.controllo };
}

/**
 * Un tocco su un valore di un asse. La coppia che ne esce e' sempre servita o incompleta, mai una coppia che il server
 * non ha: se il valore toccato non forma una voce servita con l'altro asse, l'altro asse passa al primo valore che la
 * forma (nell'ordine di schermo). Un valore che non e' fra quelli serviti non cambia niente.
 */
export function scegliSuAsse(
  assi: AssiBackgammon,
  scelta: SceltaAssi,
  asse: 'lunghezza' | 'controllo',
  valore: number | ControlloBackgammon,
): SceltaAssi {
  if (asse === 'lunghezza') {
    if (typeof valore !== 'number' || !assi.lunghezze.includes(valore)) return scelta;
    const prossima: SceltaAssi = { ...scelta, lunghezza: valore };
    if (prossima.controllo === null || chiaveDellaScelta(assi, prossima) !== null) return prossima;
    const primo = assi.controlli.find((c) => assi.voci.some((v) => v.lunghezza === valore && v.controllo === c));
    return { lunghezza: valore, controllo: primo ?? null };
  }
  if (typeof valore !== 'string' || !assi.controlli.includes(valore)) return scelta;
  const prossima: SceltaAssi = { ...scelta, controllo: valore };
  if (prossima.lunghezza === null || chiaveDellaScelta(assi, prossima) !== null) return prossima;
  const prima = assi.lunghezze.find((l) => assi.voci.some((v) => v.lunghezza === l && v.controllo === valore));
  return { lunghezza: prima ?? null, controllo: valore };
}
